import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import ArtworkCard from '../components/ArtworkCard';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { API } from '../config/api';

const categoryLabels = {
  photography: 'Photography',
  fine_art: 'Fine Art',
  film: 'Film',
};

export default function Gallery() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [artworks, setArtworks] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(category || '');

  useEffect(() => {
    setSelectedCategory(category || '');
  }, [category]);

  const fetchArtworks = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category', selectedCategory);
      if (search) params.set('search', search);
      params.set('page', page.toString());
      params.set('limit', '20');
      
      const response = await axios.get(`${API}/artworks?${params.toString()}`);
      setArtworks(response.data.artworks);
      setTotal(response.data.total);
    } catch {
      toast.error('Failed to load artworks');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, search, page]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  // Fetch user's favorite IDs
  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/my/favorites/ids`)
      .then(res => setFavoriteIds(new Set(res.data.ids)))
      .catch(() => {});
  }, [isAuthenticated]);

  const handleToggleFavorite = async (artworkId) => {
    if (!isAuthenticated) {
      toast.error('Sign in to save favorites');
      return;
    }
    try {
      const res = await axios.post(`${API}/artworks/${artworkId}/favorite`);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.favorited) next.add(artworkId);
        else next.delete(artworkId);
        return next;
      });
      toast.success(res.data.favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch {
      toast.error('Failed to update favorites');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchArtworks();
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value === 'all' ? '' : value);
    setPage(1);
  };

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            {selectedCategory ? categoryLabels[selectedCategory] : 'All Works'}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0]">
            Gallery
          </h1>
          <p className="mt-4 text-[#A1A1AA]">
            {total} {total === 1 ? 'artwork' : 'artworks'} available
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
              <Input
                type="text"
                placeholder="Search artworks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 bg-[#121212] border-white/10 text-[#F5F5F0] placeholder:text-[#71717A]"
                data-testid="search-input"
              />
            </div>
          </form>
          
          <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
            <SelectTrigger 
              className="w-full sm:w-[200px] bg-[#121212] border-white/10 text-[#F5F5F0]"
              data-testid="category-filter"
            >
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-[#F5F5F0]">All Categories</SelectItem>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <SelectItem key={value} value={value} className="text-[#F5F5F0]">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-[4/5] skeleton" />
            ))}
          </div>
        ) : artworks.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-[#71717A] text-lg">No artworks found</p>
            <p className="text-[#71717A] mt-2">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <>
            <div className="bento-grid" data-testid="artworks-grid">
              {artworks.map((artwork, index) => (
                <ArtworkCard 
                  key={artwork.id} 
                  artwork={artwork}
                  featured={index === 0}
                  isFavorited={favoriteIds.has(artwork.id)}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </div>

            {/* Pagination */}
            {total > 20 && (
              <div className="mt-12 flex justify-center gap-2">
                {[...Array(Math.ceil(total / 20))].map((_, i) => (
                  <button
                    key={`page-${i + 1}`}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 flex items-center justify-center transition-colors ${
                      page === i + 1 
                        ? 'bg-[#D4AF37] text-[#0A0A0A]' 
                        : 'bg-[#121212] text-[#A1A1AA] hover:text-[#F5F5F0]'
                    }`}
                    data-testid={`page-${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
